import { ReactElement, useContext, useEffect, useState } from "react";
import { ITask } from "../types";
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField
} from "@mui/material";
import Select from "@mui/material/Select";
import { SystemContext } from "../../../../context/systemContext";
import { IArea, ISchemaNode, IZone } from "../../../../types";

const TitleLength = 10;


interface IProps {
  data: ITask;
  setData: (task: ITask) => void;
}

interface IAreaOption {
  name: string;
  value: number;
  data: ISchemaNode;
}

interface IZoneOption {
  name: string;
  value: number;
  areaID: number;
  data: ISchemaNode;
}

const TaskInfo = ({ data, setData }: IProps): ReactElement => {
  const systemContext = useContext(SystemContext);
  const [areas, setAreas] = useState<IAreaOption[]>([]);
  const [zones, setZones] = useState<IZoneOption[]>([]);


  useEffect(() => {
    if (systemContext) {
      const as: IAreaOption[] = [];
      const zs: IZoneOption[] = [];

      systemContext.schema[0].children?.forEach(a => {
        as.push({ name: a.name, value: a.nodeID, data: a });
        a.children?.forEach(z => {
          zs.push({ name: z.name, value: z.nodeID, areaID: a.nodeID, data: z });
        });
      });

      setAreas(as);
      setZones(zs);
    }
  }, [systemContext.schema]);

  const onChange = (field: keyof ITask, value: any) => {
    if (field === "zone") {
      let zone: IZone | null = null;
      if (value !== -1){
        const option = zones.find(z => z.value === value);
        if (option) {
          zone = {
            zoneID: option.data.nodeID,
            areaID: option.areaID,
            code: option.data.code,
            name: option.data.name,
            color: option.data.color
          };
        }
      }

      setData({ ...data, [field]: zone });
    } else {
      setData({ ...data, [field]: value });
    }
  };

  const onSelectedArea = (areaID: number) => {
    let area: IArea | null = null;
    if (areaID !== -1){
      const option = areas.find(a => a.value === areaID);
      if (option) {
        area = {
          areaID,
          code: option.data.code,
          name: option.data.name,
          color: option.data.color
        };
      }
    }

    setData({
      ...data,
      area,
      zone: null
    });
  };

  return (
    <div className="display-flex-column p-1 m-1 gap-3">

      <TextField
        variant="standard"
        placeholder={"Title"}
        label={"Title"}
        value={data.title}
        disabled={data.approvedDate !== null}
        error={data.title.length < TitleLength}
        helperText={data.title.length < TitleLength ? `Title length must be > ${TitleLength}` : undefined}
        onChange={(event) => onChange("title", event.target.value)}/>

      <div className="display-flex-row flex-nowrap">
        <FormControl sx={{ width: 260, marginRight: 6 }}>
          <InputLabel>Area</InputLabel>
          <Select
            variant="standard"
            value={areas.length > 0 && data.area ? data.area.areaID ?? -1 : -1}
            displayEmpty={true}
            disabled={data.approvedDate !== null}
            onChange={(event) => onSelectedArea(event.target.value)}>
            <MenuItem value={-1}>None</MenuItem>
            {areas.map(a => {
              return (
                <MenuItem key={`task-area-${a.value}`} value={a.value}>{a.name}</MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl sx={{ width: 260, marginLeft: 6 }}>
          <InputLabel>Zone</InputLabel>
          <Select
            variant="standard"
            value={zones.length > 0 && data.zone ? data.zone.zoneID ?? -1 : -1}
            displayEmpty={true}
            disabled={data.approvedDate !== null}
            onChange={(event) => onChange("zone", event.target.value)}>
            <MenuItem value={-1}>None</MenuItem>
            {zones.filter(z => data.area && z.areaID === data.area.areaID).map(z => {
              return (
                <MenuItem key={`task-zone-${z.value}`} value={z.value}>{z.name}</MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>

      <FormControl sx={{ width: 260, marginRight: 6 }}>
        <InputLabel>Priority</InputLabel>
        <Select
          variant="standard"
          value={data.priority}
          displayEmpty={true}
          disabled={data.approvedDate !== null || data.taskID !== undefined}
          onChange={(event) => onChange("priority", event.target.value as number)}>
          <MenuItem value={0}>Low</MenuItem>
          <MenuItem value={1}>Medium</MenuItem>
          <MenuItem value={2}>Important</MenuItem>
          <MenuItem value={3}>Urgent</MenuItem>
        </Select>
      </FormControl>

      <TextField
        variant="standard"
        placeholder={"Description"}
        label={"Description"}
        value={data.description}
        multiline={true}
        rows={5}
        disabled={data.approvedDate !== null}
        onChange={event => onChange("description", event.target.value)}/>

    </div>
  );
};

export default TaskInfo;
